import React from 'react';
import { Button, LoaderScreen, Typography } from '@/components/common';
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { GET_LAWYER_RETAINER_INFO } from '@/api';
import { casesLawyerService } from '@/api/casesLawyer';
import { RetainerBody } from '@/components/screens/retainer';
import { InputText } from '@/components/form';
import { useForm } from 'react-hook-form';
import Link from 'next/link';
import { Cormorant_Garamond } from 'next/font/google';
import { Category } from '@/interfaces';
import { RetainerSecurityBody } from '@/components/screens/retainer/security/RetainerSecurityBody';
import { RetainerHeader } from '@/components/screens/retainer/RetainerHeader';

export const cormorant = Cormorant_Garamond({
	subsets: ['latin'],
	weight: ['400'],
	style: ['normal'],
	display: 'swap',
	preload: true,
	variable: '--font-camorant',
});

interface Props {
	caseId: string | undefined;
	caseCategory: Category | undefined;
}

export const RetainerPreview: React.FC<Props> = ({ caseId, caseCategory }) => {
	const { register } = useForm();

	const { data: retainer } = useQuery({
		queryKey: [GET_LAWYER_RETAINER_INFO, caseId],
		queryFn: () => casesLawyerService.getRetainerByCaseId(caseId as string),
		enabled: !!caseId,
	});

	const caseName = retainer?.data.caseName ?? '';
	const templateInfo = retainer?.data.templateInfo;
	const retainerInfo = retainer?.data.retainerInfo;
	const categoryInfo = retainer?.data.categoryName;

	const name = templateInfo?.name ?? '';
	const firmName = templateInfo?.firmName ?? '';
	const fullName = templateInfo?.fullName ?? '';
	const companyName = templateInfo?.companyName ?? '';
	const companyAlias = templateInfo?.companyAlias ?? '';
	const eligibilityCriteria = templateInfo?.eligibilityCriteria ?? '';
	const requiredDocuments = templateInfo?.requiredDocuments ?? '';

	const lawyerSignature = retainerInfo?.lawyerSign ?? '';

	if (caseCategory === undefined) return <LoaderScreen />;

	return (
		<div className="bg-white">
			<RetainerHeader caseName={caseName} category={categoryInfo} />
			<div className="max-w-[904px] m-auto pt-[88px] pb-[112px]">
				{caseCategory === 'Securities' ? (
					<RetainerSecurityBody name={name} fullName={fullName} />
				) : (
					<RetainerBody
						name={name}
						firmName={firmName}
						fullName={fullName}
						companyName={companyName}
						companyAlias={companyAlias}
						requiredDocuments={requiredDocuments}
						eligibilityCriteria={eligibilityCriteria}
					/>
				)}
				<div className="flex">
					<div className="flex-1 hidden md:block" />
					<div className="flex-1 flex flex-col">
						<div className="space-y-4">
							<Typography type="regular-18" className="text-right md:text-left">
								Best,
							</Typography>
							<Typography
								type="regular-18"
								className={clsx('text-right md:text-left')}
							>
								Signature
							</Typography>
						</div>

						<InputText
							readOnly
							name="lawyerSignature"
							value={lawyerSignature}
							register={register}
							placeholder="Type your name here"
							classNameInput={clsx('disabled:!bg-gray-5', {
								'!font-praise !text-4xl': !!lawyerSignature,
							})}
						/>
					</div>
				</div>
				<div className="flex">
					<div className="flex-1 mt-8 md:mt-auto">
						<div className="space-y-4">
							<Typography type="regular-18" className="uppercase font-semibold">
								Accepted and agreed to:
							</Typography>
							<Typography type="regular-18">Signature</Typography>

							<InputText
								disabled
								name="userSignature"
								register={register}
								placeholder="Type your name here"
								classNameInput={clsx('disabled:!bg-gray-5')}
							/>

							<Typography type="regular-18">Date</Typography>
							<InputText
								disabled
								readOnly
								name="userDate"
								register={register}
								placeholder="mm/dd/yyyy"
								classNameInput="disabled:!bg-gray-5"
							/>
						</div>
					</div>
					<div className="flex-1 hidden md:block" />
				</div>
				<div className={clsx('flex justify-center mt-10')}>
					<Button
						disabled
						type="submit"
						decoration="primary"
						size="medium"
						className="!w-48 !h-16"
					>
						Submit
					</Button>
				</div>
				<div className="flex justify-center mt-20">
					<Link href={'https://lowey.com/'} target="_blank">
						<Typography
							type="regular-18"
							className={`!text-[#236A96] ${cormorant.className}`}
						>
							www.lowey.com
						</Typography>
					</Link>
				</div>
			</div>
		</div>
	);
};
